/**
 * Global Currency Formatting Utility
 * Provides consistent currency formatting across the application based on user preferences
 */

export type CurrencyType = 
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'JPY'
  | 'CAD'
  | 'AUD'
  | 'CHF'
  | 'CNY'
  | 'INR'
  | 'MXN'
  | 'BRL'
  | 'ZAR'
  | 'SEK'
  | 'NOK'
  | 'DKK'
  | 'PLN'
  | 'RUB'
  | 'TRY'
  | 'KRW'
  | 'SGD';

export interface CurrencySettings {
  currency: CurrencyType;
  locale?: string;
}

const CURRENCY_CONFIG: Record<CurrencyType, { symbol: string; locale: string }> = {
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'de-DE' },
  GBP: { symbol: '£', locale: 'en-GB' },
  JPY: { symbol: '¥', locale: 'ja-JP' },
  CAD: { symbol: 'CA$', locale: 'en-CA' },
  AUD: { symbol: 'A$', locale: 'en-AU' },
  CHF: { symbol: 'CHF', locale: 'de-CH' },
  CNY: { symbol: '¥', locale: 'zh-CN' },
  INR: { symbol: '₹', locale: 'en-IN' },
  MXN: { symbol: 'MX$', locale: 'es-MX' },
  BRL: { symbol: 'R$', locale: 'pt-BR' },
  ZAR: { symbol: 'R', locale: 'en-ZA' },
  SEK: { symbol: 'kr', locale: 'sv-SE' },
  NOK: { symbol: 'kr', locale: 'nb-NO' },
  DKK: { symbol: 'kr.', locale: 'da-DK' },
  PLN: { symbol: 'zł', locale: 'pl-PL' },
  RUB: { symbol: '₽', locale: 'ru-RU' },
  TRY: { symbol: '₺', locale: 'tr-TR' },
  KRW: { symbol: '₩', locale: 'ko-KR' },
  SGD: { symbol: 'S$', locale: 'en-SG' },
};

const DEFAULT_CURRENCY: CurrencyType = 'USD';

export function getCurrencySymbol(currency: CurrencyType = DEFAULT_CURRENCY): string {
  return CURRENCY_CONFIG[currency]?.symbol || '$';
}

export function getCurrencyLocale(currency: CurrencyType = DEFAULT_CURRENCY): string {
  return CURRENCY_CONFIG[currency]?.locale || 'en-US';
}

export function formatCurrency(
  amount: number | string | null | undefined,
  options?: {
    currency?: CurrencyType;
    showSymbol?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  try {
    if (amount === null || amount === undefined) {
      return formatCurrency(0, options);
    }

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numAmount)) {
      return formatCurrency(0, options);
    }

    const currency = options?.currency || DEFAULT_CURRENCY;
    const locale = getCurrencyLocale(currency);

    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: options?.minimumFractionDigits ?? 2,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    }).format(numAmount);

    return formatted;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${getCurrencySymbol(options?.currency)}${amount}`;
  }
}

export function formatNumber(
  amount: number | string | null | undefined,
  options?: {
    currency?: CurrencyType;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  try {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
    
    if (isNaN(numAmount)) {
      return '0';
    }

    const currency = options?.currency || DEFAULT_CURRENCY;
    const locale = getCurrencyLocale(currency);

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: options?.minimumFractionDigits ?? 2,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    }).format(numAmount);
  } catch (error) {
    console.error('Error formatting number:', error);
    return String(amount || 0);
  }
}
